'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import type { Project } from "../types";

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: Parameters<typeof elowenClient.createProject>[0]) => elowenClient.createProject(v), onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['projects'] }), qc.invalidateQueries({ queryKey: ['project-summaries'] })]); } });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: number; slug?: string; name?: string; path?: string; notes?: string; memoryShared?: boolean }) => elowenClient.updateProject(v.id, { slug: v.slug, name: v.name, path: v.path, notes: v.notes, memoryShared: v.memoryShared }), onSuccess: async () => { await Promise.all([qc.invalidateQueries({ queryKey: ['projects'] }), qc.invalidateQueries({ queryKey: ['project-summaries'] }), qc.invalidateQueries({ queryKey: ['projects', 'execution-targets'] })]); } });
}

/** Arrange the register. The list moves at once and goes back if the save fails; the order is the
 *  caller's own and the first project is their default, so a failed save must not leave a card where it
 *  never persisted. Saves run one after another, so the last drag is the one that is stored. */
export function useReorderProjects() {
  const qc = useQueryClient();
  const filter = { mutationKey: ['project-order'] };
  // Several saves can be waiting behind one another. Only the last to settle may put the list back or
  // refetch it: an earlier one doing so would show an order that the saves still queued are about to replace,
  // and a rollback to its snapshot would drop moves the queued saves already carry.
  const lastToSettle = () => qc.isMutating(filter) <= 1;
  return useMutation({
    mutationKey: ['project-order'],
    scope: { id: 'project-order' },
    mutationFn: (ids: number[]) => elowenClient.reorderProjects(ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ['projects'] });
      const previous = qc.getQueryData<Project[]>(['projects']);
      if (previous) {
        const byId = new Map(previous.map((project) => [project.id, project]));
        qc.setQueryData<Project[]>(['projects'], [
          ...ids.flatMap((id) => byId.get(id) ?? []),
          ...previous.filter((project) => !ids.includes(project.id)),
        ]);
      }
      return { previous };
    },
    onError: (_error, _ids, context) => { if (context?.previous && lastToSettle()) qc.setQueryData(['projects'], context.previous); },
    onSettled: () => (lastToSettle() ? qc.invalidateQueries({ queryKey: ['projects'] }) : undefined),
  });
}

export function useAdoptProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; undo?: boolean }) => elowenClient.adoptProject(v.id, v.undo === true),
    onSuccess: async (_project, v) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project-summaries'] }),
        qc.invalidateQueries({ queryKey: ['project-environment-state', v.id] }),
      ]);
    },
  });
}

/** Create a child under the open directory. `listingPath` is the actual query key that produced the
 * canonical parent, which can differ when the picker started at the server's default home directory. */
export function useCreateDirectory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { parent: string; name: string; listingPath?: string }) => elowenClient.createDir(v.parent, v.name),
    onSuccess: async (_created, v) => {
      const originalKey = v.listingPath ?? '';
      const keys = originalKey === v.parent ? [originalKey] : [originalKey, v.parent];
      await Promise.all(keys.map((key) => qc.invalidateQueries({ queryKey: ['fs-dirs', key], exact: true, refetchType: 'active' })));
    },
  });
}

/** Replace a project's shared-memory share list wholesale (admin-only). */
export function useSetProjectMemoryMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { projectId: number; userIds: number[] }) => elowenClient.setProjectMemoryMembers(v.projectId, v.userIds),
    onSuccess: async (_r, v) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['project-memory-members', v.projectId] }),
        qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryCategories }),
        qc.invalidateQueries({ queryKey: QUERY_KEYS.memories }),
      ]);
    },
  });
}

export function useRemoveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...confirmation }: { id: number } & Parameters<typeof elowenClient.removeProject>[1]) => elowenClient.removeProject(id, confirmation),
    onSuccess: ({ deletion }, { id }) => {
      qc.setQueryData<Project[]>(['projects'], current => current?.map(project => project.id === id ? { ...project, lifecycle: 'deleting', deletion } : project));
    },
    onSettled: async (_data, _error, { id }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project-summaries'] }),
        ...(_error ? [qc.invalidateQueries({ queryKey: ['project-deletion', id] })] : []),
      ]);
    },
  });
}

/**
 * Toggle a project assignment for a user. `currentlyAssigned` is the present state of the chip:
 * when the project is already assigned we unassign it, otherwise we assign it. Naming the flag
 * after the current state (rather than a bare `assigned`) keeps the toggle direction unambiguous.
 */
export function useAssignProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: number; projectId: number; currentlyAssigned: boolean }) =>
      v.currentlyAssigned ? elowenClient.unassignProject(v.userId, v.projectId) : elowenClient.assignProject(v.userId, v.projectId),
    onSettled: async (_r, _error, v) => {
      // A failed call is not proof that nothing landed: the response can be lost after the daemon applied
      // the change, so refresh the membership views either way.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['user-projects', v.userId] }),
        qc.invalidateQueries({ queryKey: ['project-users', v.projectId] }),
        qc.invalidateQueries({ queryKey: ['project-member-profiles', v.projectId] }),
        qc.invalidateQueries({ queryKey: ['project-summaries'] }),
      ]);
    },
  });
}
